import { Formik, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/button';
import FileUpload from '../../components/FileUpload';
import Input from '../../components/input';
import Select from '../../components/Select';
import { apiService } from '../../services/apiService';
import { createFuelLog, fetchFuelOverview, fuelLogsLists, getFuelStations, getTripListsDropdown } from '../../store/Slices/FuelSlice';
import { fetchVehicleDropdown } from '../../store/Slices/ServiceSlice';
import { getUserDropdown } from '../../store/Slices/UserSlice';
import '../../styles/Issue.css';
import { addFuelLogValidationSchema } from '../../utils/Schema';
import { errorNotify, successNotify } from '../../utils/toast';
import { fuelTypeOptions } from '../AssetsManagement/Vehicles/vehicleAssets.constants';

const getFiniteNumber = (...values) => {
    for (const value of values) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return null;
};

const formatExpectedFuelCost = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "";

    return String(Number(parsed.toFixed(2)));
};

const formatDateInputValue = (value) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "";

    const directDateMatch = normalizedValue.match(/^\d{4}-\d{2}-\d{2}/);
    if (directDateMatch) {
        return directDateMatch[0];
    }

    const parsedDate = new Date(normalizedValue);
    if (Number.isNaN(parsedDate.getTime())) return "";

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const extractCurrentFuelRate = (payload = {}) =>
    getFiniteNumber(
        payload,
        payload?.data,
        payload?.current_rate,
        payload?.current_fuel_rate,
        payload?.fuel_rate,
        payload?.rate,
        payload?.price
    );

const resolveExpectedFuelCost = (payload = {}, quantity = "", fallbackRate = null) => {
    const directExpectedFuelCost = getFiniteNumber(
        payload,
        payload?.data,
        payload?.expected_total_fuel_cost,
        payload?.expectedFuelCost,
        payload?.total_fuel_cost,
        payload?.totalFuelCost,
        payload?.amount
    );

    if (directExpectedFuelCost !== null) {
        return formatExpectedFuelCost(directExpectedFuelCost);
    }

    const currentFuelRate = fallbackRate ?? extractCurrentFuelRate(payload);
    const claimedQuantity = Number(quantity);

    if (currentFuelRate === null) {
        return "";
    }

    if (!Number.isFinite(claimedQuantity) || claimedQuantity <= 0) {
        return formatExpectedFuelCost(currentFuelRate);
    }

    return formatExpectedFuelCost(currentFuelRate * claimedQuantity);
};

const isFuelStationFixed = (station) => {
    if (!station) return false;
    const stationType = String(
        station?.type ?? station?.station_type ?? station?.stationType ?? ""
    ).trim().toLowerCase();
    return stationType === "fixed";
};

const FuelLogExpectedCostSync = ({ setCurrentFuelRateLoading, isStationFixed }) => {
    const { values, setFieldValue } = useFormikContext();
    const [currentFuelRateState, setCurrentFuelRateState] = useState({
        value: null,
        sourceKey: "",
    });

    const selectedFuelTypeOption =
        fuelTypeOptions.find((option) => option.value === values.fuelType) || null;
    const hasRequiredSelections = Boolean(
        isStationFixed &&
        values.vendor_id &&
        selectedFuelTypeOption?.value &&
        values.filled_at
    );
    const selectedFuelStationId = Number(values.vendor_id);
    const selectedRateSourceKey = hasRequiredSelections
        ? `${values.vendor_id || ""}-${selectedFuelTypeOption?.value || ""}-${values.filled_at || ""}`
        : "";

    useEffect(() => {
        let isActive = true;

        if (!isStationFixed) {
            setCurrentFuelRateState({ value: null, sourceKey: "" });
            setCurrentFuelRateLoading(false);
            return undefined;
        }

        if (!hasRequiredSelections) {
            setCurrentFuelRateState({ value: null, sourceKey: "" });
            setCurrentFuelRateLoading(false);
            setFieldValue("expected_total_fuel_cost", "");
            setFieldValue("fuel_price", "");
            return undefined;
        }

        setCurrentFuelRateLoading(true);
        setCurrentFuelRateState({ value: null, sourceKey: selectedRateSourceKey });
        setFieldValue("expected_total_fuel_cost", "");
        setFieldValue("fuel_price", "");

        apiService
            .getCurrentFuelRate({
                fuel_type: selectedFuelTypeOption.value,
                fuel_station_id: selectedFuelStationId,
                changed_at: values.filled_at,
            })
            .then((response) => {
                if (!isActive) return;

                const responseData = response?.data ?? response ?? {};
                const nextCurrentFuelRate = extractCurrentFuelRate(responseData);

                setCurrentFuelRateState({
                    value: nextCurrentFuelRate,
                    sourceKey: selectedRateSourceKey,
                });
                setFieldValue(
                    "expected_total_fuel_cost",
                    resolveExpectedFuelCost(responseData, "", null)
                );
                setFieldValue(
                    "fuel_price",
                    nextCurrentFuelRate !== null ? nextCurrentFuelRate : ""
                );
            })
            .catch(() => {
                if (!isActive) return;

                setCurrentFuelRateState({
                    value: null,
                    sourceKey: selectedRateSourceKey,
                });
                setFieldValue("expected_total_fuel_cost", "");
                setFieldValue("fuel_price", "");
            })
            .finally(() => {
                if (isActive) {
                    setCurrentFuelRateLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [
        isStationFixed,
        hasRequiredSelections,
        values.filled_at,
        selectedFuelStationId,
        selectedFuelTypeOption?.value,
        selectedRateSourceKey,
        setCurrentFuelRateLoading,
        setFieldValue,
    ]);

    useEffect(() => {
        if (
            !isStationFixed ||
            currentFuelRateState.value === null ||
            currentFuelRateState.sourceKey !== selectedRateSourceKey
        ) {
            return;
        }

        setFieldValue(
            "expected_total_fuel_cost",
            resolveExpectedFuelCost({}, values.quantity, currentFuelRateState.value)
        );
    }, [
        isStationFixed,
        currentFuelRateState.sourceKey,
        currentFuelRateState.value,
        selectedRateSourceKey,
        setFieldValue,
        values.quantity,
    ]);

    return null;
};

const normalizeFuelTypeValue = (value) => {
    const normalizedValue = String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    return fuelTypeOptions.find(
        (option) => String(option.value).toLowerCase() === normalizedValue
    )?.value || "";
};

const getTripFuelType = (tripOption = {}) =>
    normalizeFuelTypeValue(
        tripOption?.vehicle?.fuel_type ||
        tripOption?.vehicle?.fuelType ||
        tripOption?.fuel_type ||
        tripOption?.fuelType
    );

const getVehicleFuelType = (vehicleOption = {}) =>
    normalizeFuelTypeValue(vehicleOption?.fuel_type || vehicleOption?.fuelType);

const FuelTypeFromVehicleSync = ({ vehicleOptions = [] }) => {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        if (!values.vehicle_id) {
            if (values.fuelType) {
                setFieldValue("fuelType", "");
            }
            return;
        }

        const selectedVehicle = vehicleOptions.find((vehicleOption) =>
            String(vehicleOption?.value ?? vehicleOption?.id ?? "") === String(values.vehicle_id)
        );
        const nextFuelType = getVehicleFuelType(selectedVehicle);

        if (nextFuelType && nextFuelType !== values.fuelType) {
            setFieldValue("fuelType", nextFuelType);
        }
    }, [setFieldValue, values.fuelType, values.vehicle_id, vehicleOptions]);

    return null;
};

const AddFuelLog = ({ handleClose }) => {
    const dispatch = useDispatch();
    const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));
    const requiredStar = <span className="required_star">*</span>;
    const [currentFuelRateLoading, setCurrentFuelRateLoading] = useState(false);

    const { loading, vehicleData } = useSelector((state) => state.services);
    const { createFuelLogLoading, getFuelStationsData, getFuelStationsLoading, getTripListsDropdownData, getTripListsDropdownLoading } = useSelector((state) => state.fuel);
    const { userDropdownLoading, userDropdownData } = useSelector((state) => state.users)

    const initialValues = {
        trip_id: null,
        vehicle_id: null,
        reported_by: userDetails?.id || '',
        filled_at: '',
        quantity: '',
        fuelType: '',
        expected_total_fuel_cost: '',
        total_amount: '',
        vendor_id: null,
        attachments: [],
        fuel_price: ''
    };

    useEffect(() => {
        dispatch(fetchVehicleDropdown());
        dispatch(getUserDropdown());
        dispatch(getFuelStations());
        dispatch(getTripListsDropdown());
    }, [dispatch]);

    const userOptions = userDropdownData && userDropdownData.map(user => ({
        value: user.value,
        label: user.label,
        image: user.image,
        showIcon: true
    }));

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const selectedStation = getFuelStationsData?.find(
                (station) => String(station?.value ?? station?.id) === String(values.vendor_id)
            );
            const isStationFixed = isFuelStationFixed(selectedStation);

            if (
                isStationFixed &&
                values.vendor_id &&
                values.fuelType &&
                (values.expected_total_fuel_cost === '' ||
                    values.expected_total_fuel_cost === null ||
                    values.expected_total_fuel_cost === undefined)
            ) {
                setSubmitting(false);
                errorNotify("Expected total fuel cost could not be fetched. Please reselect fuel station or fuel type.");
                return;
            }

            const payload = {
                trip_id: parseInt(values.trip_id),
                vehicle_id: parseInt(values.vehicle_id),
                fuel_station_id: parseInt(values.vendor_id),
                refuel_at: values.filled_at,
                fuel_type: String(values.fuelType || '').trim(),
                claimed_fueled: parseFloat(values.quantity),
                expected_total_fuel_cost:
                    values.expected_total_fuel_cost !== '' &&
                    values.expected_total_fuel_cost !== null &&
                    values.expected_total_fuel_cost !== undefined
                        ? parseFloat(values.expected_total_fuel_cost)
                        : null,
                total_fuel_cost: parseFloat(values.total_amount),
                fuel_price: values.fuel_price !== '' && values.fuel_price !== null && values.fuel_price !== undefined ? parseFloat(values.fuel_price) : null,
                images: values.attachments?.length > 0 ? values.attachments.map((file) => file.fileName) : [],
            };

            await dispatch(createFuelLog(payload)).unwrap();
            await dispatch(fetchFuelOverview()).unwrap();

            successNotify('Fuel log added successfully');
            handleClose();
            resetForm();

            dispatch(fuelLogsLists({ page: 1, limit: 10, search_text: '' }));
        } catch (error) {
            errorNotify(error.message || "Failed to add fuel log");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={addFuelLogValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
        >
            {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
                setFieldTouched,
                isSubmitting,
                submitForm,
                submitCount
            }) => {
                const selectedTripOption =
                    getTripListsDropdownData?.find((tripOption) => tripOption.value === values.trip_id) || null;
                const selectedFuelStationOption =
                    getFuelStationsData?.find((station) => String(station?.value ?? station?.id) === String(values.vendor_id)) || null;
                const isStationFixed = isFuelStationFixed(selectedFuelStationOption);
                const refuelDateMinValue = formatDateInputValue(selectedTripOption?.start_at);
                const updateAttachments = (updater) => {
                    const nextFiles =
                        typeof updater === 'function' ? updater(values.attachments || []) : updater;
                    setFieldValue('attachments', nextFiles);
                    setFieldTouched('attachments', true, false);
                };
                const arePrimaryFieldsComplete = Boolean(
                    values.trip_id &&
                    values.vehicle_id &&
                    values.vendor_id &&
                    values.fuelType
                );

                return (
                    <div className="add-fuel-log-form">
                        <FuelLogExpectedCostSync
                            setCurrentFuelRateLoading={setCurrentFuelRateLoading}
                            isStationFixed={isStationFixed}
                        />
                        <FuelTypeFromVehicleSync vehicleOptions={vehicleData || []} />
                        <Row>
                            <Col md={6}>
                                <Select
                                    label="Trip ID"
                                    placeholder="Select Trip ID"
                                    options={getTripListsDropdownData}
                                    name="trip_id"
                                    loading={getTripListsDropdownLoading}
                                    value={selectedTripOption}
                                    onChange={(option) => {
                                        setFieldValue('trip_id', option?.value || '');
                                        setFieldValue('vehicle_id', option?.vehicle?.id || option?.vehicle_id || option?.vehicleId || '');
                                        setFieldValue('fuelType', getTripFuelType(option));
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.trip_id}
                                    touched={touched.trip_id}
                                    required
                                    isSearch={true}
                                />
                            </Col>

                            <Col md={6}>
                                <Select
                                    label="Vehicle ID"
                                    placeholder="Select Vehicle ID"
                                    options={vehicleData}
                                    loading={loading}
                                    name="vehicle_id"
                                    value={vehicleData?.find(v => v.value === values.vehicle_id)}
                                    onChange={(option) => setFieldValue('vehicle_id', option?.value || '')}
                                    onBlur={handleBlur}
                                    error={errors.vehicle_id}
                                    touched={touched.vehicle_id}
                                    required
                                    isSearch={true}
                                    isDisabled
                                />
                            </Col>
                            <Col md={6}>
                                <Select
                                    label="Filling Station"
                                    placeholder="Select Filling Station"
                                    options={getFuelStationsData}
                                    loading={getFuelStationsLoading}
                                    name="vendor_id"
                                    value={selectedFuelStationOption}
                                    onChange={(option) => {
                                        setFieldValue('vendor_id', option?.value || '');
                                        const nextIsFixed = isFuelStationFixed(option);
                                        if (!nextIsFixed) {
                                            setFieldValue('expected_total_fuel_cost', '');
                                            setFieldValue('fuel_price', '');
                                        }
                                    }}
                                    onBlur={handleBlur}
                                    error={errors.vendor_id}
                                    touched={touched.vendor_id}
                                    required
                                    isSearch={true}
                                />
                            </Col>
                            <Col md={6}>
                                <Select
                                    label="Fuel Type"
                                    placeholder="Select Fuel Type"
                                    options={fuelTypeOptions}
                                    value={
                                        fuelTypeOptions.find((option) => option.value === normalizeFuelTypeValue(values.fuelType)) ||
                                        null
                                    }
                                    name="fuelType"
                                    onChange={(option) =>
                                        setFieldValue("fuelType", option ? option.value : "")
                                    }
                                    onBlur={handleBlur}
                                    error={errors.fuelType}
                                    touched={touched.fuelType}
                                    required
                                    isSearch={false}
                                    isDisabled={true}
                                />
                            </Col>
                            <Col md={12}>
                                <Input
                                    label="Refuel Date"
                                    type="date"
                                    name="filled_at"
                                    placeholder="YY-MM-DD"
                                    value={values.filled_at}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.filled_at}
                                    touched={touched.filled_at}
                                    disabled={!values.trip_id}
                                    minValue={refuelDateMinValue}
                                    required
                                />
                            </Col>

                            <Col md={6}>
                                <Input
                                    label="Liters Claimed (L)"
                                    placeholder="00"
                                    type="number"
                                    name="quantity"
                                    value={values.quantity}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.quantity}
                                    touched={touched.quantity}
                                    disabled={!values.trip_id}
                                    required
                                />
                            </Col>

                            <Col md={6}>
                                <Input
                                    label="Fuel Price"
                                    placeholder={
                                        isStationFixed
                                            ? currentFuelRateLoading
                                                ? "Fetching fuel price..."
                                                : "Auto-fetched for fixed station"
                                            : "Enter fuel price per liter"
                                    }
                                    type="number"
                                    name="fuel_price"
                                    value={values.fuel_price}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.fuel_price}
                                    touched={touched.fuel_price}
                                    disabled={isStationFixed}
                                    required={false}
                                    showExtraText
                                    extraText="PKR/L"
                                />
                            </Col>

                            <Col md={6}>
                                <Input
                                    label="Expected Total Fuel Cost"
                                    placeholder={
                                        isStationFixed
                                            ? currentFuelRateLoading
                                                ? "Fetching expected fuel cost..."
                                                : "Enter liters claimed to auto-calculate"
                                            : "Enter expected total fuel cost"
                                    }
                                    type="number"
                                    name="expected_total_fuel_cost"
                                    value={values.expected_total_fuel_cost}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.expected_total_fuel_cost}
                                    touched={touched.expected_total_fuel_cost}
                                    disabled={isStationFixed}
                                    required={false}
                                    showExtraText
                                    extraText="PKR"
                                />
                            </Col>

                            <Col md={6}>
                                <Input
                                    label="Total Cost Paid"
                                    placeholder="00"
                                    type="number"
                                    name="total_amount"
                                    value={values.total_amount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={errors.total_amount}
                                    touched={touched.total_amount}
                                    disabled={!values.trip_id}
                                    required
                                    showExtraText
                                    extraText="PKR"
                                />
                            </Col>

                            <Col md={12}>
                                <div className='issue_file_upload_wrapper'>
                                    <label>Attachments {requiredStar}</label>
                                    <FileUpload
                                        destination="fuel-logs"
                                        files={values.attachments}
                                        setFiles={updateAttachments}
                                        maxFileSize={10}
                                        className='issue_file_upload'
                                        disabled={!values.trip_id}
                                    />
                                    {(touched.attachments || submitCount > 0) && errors.attachments && (
                                        <div
                                            className="error-message"
                                            style={{
                                                color: '#ff0000de',
                                                fontSize: '12px',
                                                marginTop: '3px',
                                                marginLeft: '5px',
                                            }}
                                        >
                                            {errors.attachments}
                                        </div>
                                    )}
                                </div>
                            </Col>

                            <Col md={12}>
                                <Select
                                    label="Reported By"
                                    placeholder="Please Select"
                                    options={userOptions}
                                    loading={userDropdownLoading}
                                    name="reported_by"
                                    value={userOptions.find(option => option.value === values.reported_by)}
                                    onChange={(selectedOption) => setFieldValue('reported_by', selectedOption?.value || '')}
                                    onBlur={handleBlur}
                                    error={errors.reported_by}
                                    touched={touched.reported_by}
                                    isSearch={false}
                                    isDisabled={true}
                                />
                            </Col>

                            <Col md={12}>
                                <div className='d-flex gap-2 mt-3'>
                                    <Button
                                        cancelBtn={handleClose}
                                        submitBtn={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            submitForm();
                                        }}
                                        loading={isSubmitting || createFuelLogLoading}
                                        disabled={isSubmitting || createFuelLogLoading || currentFuelRateLoading}
                                        submitText="Submit"
                                        type="button"
                                        shouldRow={false}
                                        buttonExtraCss="fuel_add_btn"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            }}
        </Formik>
    );
};

export default AddFuelLog;
